import type { ThemeAssets } from '../types';

export type MascotAssets = NonNullable<ThemeAssets['mascot']>;
export type MascotCodec = 'vp9' | 'hevc';

/** The two facts about the browser the codec choice depends on (injectable for tests). */
export interface MascotCodecEnv {
  vendor: string;
  canPlayType(type: string): string;
}

export function browserCodecEnv(): MascotCodecEnv | null {
  if (typeof document === 'undefined' || typeof navigator === 'undefined') return null;
  const v = document.createElement('video');
  if (typeof v.canPlayType !== 'function') return null;
  return { vendor: navigator.vendor ?? '', canPlayType: (t) => v.canPlayType(t) };
}

/**
 * Which clip set the browser can show with transparency, or `null` for "poster only".
 * WebKit (Safari and every iOS browser) reports VP9 as playable yet discards the WebM alpha plane — the mascot
 * would render on an opaque grey card — so it gets the HEVC-with-alpha `.mov` variant when the theme ships one.
 * Every other engine plays VP9-alpha WebM.
 */
export function pickMascotCodec(m: Pick<MascotAssets, 'idle' | 'hevc'>, env: MascotCodecEnv | null = browserCodecEnv()): MascotCodec | null {
  if (!env) return null;
  if (/apple/i.test(env.vendor)) return m.hevc?.idle && env.canPlayType('video/mp4; codecs="hvc1"') !== '' ? 'hevc' : null;
  return m.idle && env.canPlayType('video/webm; codecs="vp9"') !== '' ? 'vp9' : null;
}
